<script setup lang="ts">
import { computed } from 'vue'
import type { EngineFitmentGroup } from '../../types'

const props = defineProps<{
  groups: EngineFitmentGroup[]
  truncated?: boolean
}>()

interface Row {
  manufacturer: string
  model: string
  type: string
  engineCode: string
  restrictions: { description: string; value: string }[]
}

// Collapse near-duplicate engine rows that share manufacturer / model / type and the same
// restrictions, merging their engine codes — TecDoc lists many engine variants per model.
const rows = computed<Row[]>(() => {
  const seen = new Map<string, { row: Row; codes: Set<string> }>()
  for (const g of props.groups) {
    const restrSig = g.restrictions
      .map(r => `${r.description}=${r.value}`)
      .sort()
      .join('|')
    const key = `${g.manufacturer}|${g.model}|${g.type}|${restrSig}`
    const entry = seen.get(key)
    const codes = (g.engineCode || '').split('/').map(c => c.trim()).filter(Boolean)
    if (entry) {
      codes.forEach(c => entry.codes.add(c))
    } else {
      seen.set(key, {
        row: {
          manufacturer: g.manufacturer,
          model: g.model,
          type: g.type,
          engineCode: '',
          restrictions: g.restrictions,
        },
        codes: new Set(codes),
      })
    }
  }
  return Array.from(seen.values()).map(({ row, codes }) => ({
    ...row,
    engineCode: Array.from(codes).join(', '),
  }))
})
</script>

<template>
  <div class="fitment-table">
    <p class="fitment-table__intro">
      Denne delen er oppført for følgende kjøretøy/motorer. Kontroller mot ditt eget
      kjøretøy (chassisnr./årsmodell) før kjøp:
    </p>
    <div class="fitment-table__scroll">
      <table>
        <thead>
          <tr>
            <th>Merke</th>
            <th>Modell</th>
            <th>Motor</th>
            <th>Begrensning</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, i) in rows" :key="i">
            <td>{{ r.manufacturer }}</td>
            <td>{{ r.model }}</td>
            <td>
              {{ r.type }}
              <span v-if="r.engineCode" class="fitment-table__code">({{ r.engineCode }})</span>
            </td>
            <td>
              <template v-if="r.restrictions.length">
                <div
                  v-for="(c, j) in r.restrictions"
                  :key="j"
                  class="fitment-table__restr"
                >
                  {{ c.description }}: <strong>{{ c.value }}</strong>
                </div>
              </template>
              <span v-else class="fitment-table__none">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="truncated" class="fitment-table__more">
      Viser kun de første motorene. Kontakt oss for full oversikt.
    </p>
  </div>
</template>

<style scoped lang="scss">
.fitment-table {
  &__intro {
    font-size: 0.82rem;
    color: #4b5563;
    margin: 0 0 0.6rem;
  }

  &__scroll {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
  }

  th {
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
    padding: 6px 10px;
    white-space: nowrap;
  }

  td {
    padding: 6px 10px;
    border-bottom: 1px solid #f1f5f9;
    color: #111827;
    vertical-align: top;
  }

  tr:hover td {
    background: #f9fafb;
  }

  &__code {
    color: #6b7280;
    font-family: monospace;
    font-size: 0.76rem;
  }

  &__restr {
    color: #92400e;
    line-height: 1.35;
  }

  &__none {
    color: #9ca3af;
  }

  &__more {
    font-size: 0.78rem;
    color: #6b7280;
    margin: 0.5rem 0 0;
  }
}
</style>
